<template>
  <div id="app all-exchanges-search">
    <div v-if="updating" class="loader">
      <div class="loading loading-lg"></div>
    </div>

    <template v-else>
      <div class="scroll-top" @click="scrollToTop">(^)</div>

      <h3>Everything Bookmarks</h3>

      <p>
        These are bookmarks you have made from Everything pages. You can reorder
        bookmarks using the
        <span class="fas fa-arrows-alt-v move"></span> icon, and resize the
        bookmark table columns by dragging on the column header bars. For backup
        instructions, see the <a href="/#/">main app page</a>.
      </p>

      <div class="toast toast-warning mb-2">
        Bookmarks you've made from individual exchange app pages will
        <strong>not</strong> show up here – they are contained to their
        individual exchange pages.
      </div>

      <label class="form-switch form-inline">
        <input type="checkbox" v-model="togglePrompts" />
        <i class="form-icon"></i> Use toggles for request details
      </label>

      <label class="form-switch form-inline">
        <input type="checkbox" v-model="toggleChars" />
        <i class="form-icon"></i> Use toggles for character list
      </label>

      <label class="form-switch form-inline">
        <input type="checkbox" v-model="toggleFont" />
        <i class="form-icon"></i> 80% font-size for 125% prompt content
      </label>

      <table v-table-resizer class="prompts" :class="{ 'text-small': toggleFont }">
        <thead>
          <tr>
            <th class="fave bookmark"></th>
            <th class="fandom">Exchange, Username, Fandom</th>
            <th>Characters & Relationships</th>
            <th class="prompts">Prompts</th>
          </tr>
        </thead>
        <tbody v-model="bookmarks" is="draggable" tag="tbody" handle=".move">
          <tr v-for="(prompt, index) in bookmarks" :key="index" :class="{ odd: index % 2 !== 0 }">
            <td class="fave">
              <span class="fas fa-arrows-alt-v move pl-1 mr-1"></span>
              <button aria-label="Toggle prompt bookmark" class="bookmark" @click="toggleEverythingBookmark(prompt)">
                <span aria-label="Remove bookmark" v-if="hasEverythingBookmark(prompt)" class="fas fa-heart"></span>
                <span v-else class="far fa-heart" aria-label="Bookmark"></span>
              </button>
            </td>
            <td>
              <a href="#" @click.prevent="
                $router.push({
                  path: `/${prompt.exchange}`,
                })
                ">{{ prompt.exchange }}</a>

              <div class="divider"></div>

              <a href="#" @click.prevent="
                $router.push({
                  path: `/${prompt.exchange}/user/${prompt.username}`,
                })
                ">{{ prompt.username }}</a>
              <div class="divider"></div>

              <strong>{{ prompt.fandom }}</strong>
            </td>
            <td>
              <button class="btn btn-link" v-if="toggleChars" @click="toggleChar(prompt.sha)">
                <span v-if="shouldShowChars(prompt.sha)">
                  <i class="icon icon-arrow-down mr-1"></i> Hide characters
                </span>
                <span v-else>
                  <i class="icon icon-arrow-right mr-1"></i> Show characters
                </span>
              </button>

              <character-list :characters="prompt.characters" :relationships="prompt.relationships" :hide-title="true"
                v-if="!toggleChars || shouldShowChars(prompt.sha)"></character-list>
            </td>
            <td class="prompt">
              <chips v-if="prompt.fandoms && prompt.fandoms.length > 1"
                :tags="prompt.fandoms.filter(f => f != prompt.fandom)" title="Other Fandoms"></chips>
              <div class="mb-2" v-if="prompt.title">
                <strong>Title:</strong>&nbsp;<span>{{ prompt.title }}</span>
              </div>


              <button class="btn btn-link" v-if="togglePrompts" @click="togglePrompt(prompt.sha)">
                <span v-if="shouldShowPrompt(prompt.sha)">
                  <i class="icon icon-arrow-down mr-1"></i> Hide details
                </span>
                <span v-else>
                  <i class="icon icon-arrow-right mr-1"></i> Show details
                </span>
              </button>
              <template v-if="!togglePrompts || shouldShowPrompt(prompt.sha)">
                <chips :tags="prompt.warnings" title="Warnings"></chips>
                <chips :tags="prompt.ratings" title="Ratings"></chips>
                <chips :tags="prompt.freeforms" title="Freeforms"></chips>
                <chips :tags="prompt.optionalTags || prompt.optional_tags" title="Optional Tags"></chips>

                <div class="mb-2" v-if="prompt.letter && prompt.letter.length">
                  <a :href="prompt.letter" target="blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                    <sup>
                      <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
                </div>
                <div v-if="prompt.prompt" v-html="prompt.prompt" class="prompt-text"></div>
                <p v-if="!prompt.prompt">
                  No optional details given.
                  <span v-if="prompt.letter">
                    However, they do have a
                    <a :href="prompt.letter" target="blank">letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                      <sup>
                        <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>.
                  </span>
                </p>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import { find, includes, union, remove } from "lodash-es";
import chips from "../components/chips.vue";
import characterList from "../components/character-list.vue";
import tableResizer from "../directives/tableResizer";
import draggable from "vuedraggable";
import utils from "../components/utils.js";

import { mapGetters } from "vuex";

export default {
  name: "everythingBookmarks",
  directives: {
    tableResizer
  },
  components: {
    chips,
    characterList,
    draggable
  },
  beforeMount() {
    this.loadBookmarks();
  },
  activated() {
    this.$store.commit("setExchangeId", "");
  },
  data() {
    return {
      updating: false,
      togglePrompts: true,
      toggleChars: false,
      showPrompt: [],
      showChars: [],
      toggleFont: false
    };
  },
  computed: {
    ...mapGetters(["everythingBookmarks", "exchanges"]),

    bookmarks: {
      get() {
        return this.$store.state.everythingBookmarks;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.$store.commit("setEverythingBookmarks", value);
        this.$localStorage.set(
          `everythingbookmarks`,
          JSON.stringify(value || [])
        );
      }
    },
    filterCount() {
      let count = 0;

      if (this.fandomFilter && this.fandomFilter.length) {
        count++;
      }
      if (this.freeformFilter && this.freeformFilter.length) {
        count++;
      }
      if (this.relationshipFilter && this.relationshipFilter.length) {
        count++;
      }
      if (this.optionalTagFilter && this.optionalTagFilter.length) {
        count++;
      }
      if (this.characterFilter && this.characterFilter.length) {
        count++;
      }
      if (this.promptFilter && this.promptFilter.length) {
        count++;
      }
      return count;
    }
  },
  methods: {
    hasEverythingBookmark: utils.hasEverythingBookmark,
    toggleEverythingBookmark: utils.toggleEverythingBookmark,
    removeEverythingBookmark: utils.removeEverythingBookmark,
    exchangeDate: utils.exchangeDate,
    loadBookmarks() {
      const bookmarksJson = this.$localStorage.get(`everythingbookmarks`);
      let bookmarks = [];
      if (bookmarksJson) {
        try {
          bookmarks = JSON.parse(bookmarksJson);
        } catch {
          bookmarks = [];
        }
        this.$store.commit("setEverythingBookmarks", bookmarks);
      }
    },
    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    togglePrompt(sha) {
      if (this.shouldShowPrompt(sha)) {
        this.showPrompt = remove(this.showPrompt, sha);
      } else {
        this.showPrompt.push(sha);
      }
    },
    toggleChar(sha) {
      if (this.shouldShowChars(sha)) {
        this.showChars = remove(this.showChars, sha);
      } else {
        this.showChars.push(sha);
      }
    },
    shouldShowPrompt(sha) {
      return includes(this.showPrompt, sha);
    },
    shouldShowChars(sha) {
      return includes(this.showChars, sha);
    }
  }
};
</script>


<style lang="scss" scoped>
@import "../styles/variables.scss";

.fave {
  width: 40px;
}

.field-select {
  flex: 0 1 auto;
  width: 33%;
}

.prompt {
  margin: 0.8em 0;
  // border-bottom: 1px solid $outline-light;

  &:last-child {
    .break {
      display: none;
    }
  }
}

.flex {
  margin-top: 0.4em;
  display: flex;
}

ul {
  li {
    margin: 0;
    padding: 4px 0;
    border-bottom: 1px solid $outline-light;

    &:last-child {
      border: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }
}

.toggle {
  width: 150px;
  height: 35px;
  display: block;
}

.break {
  width: 80%;
  margin: 0.8em auto;
}

.text-small {
  font-size: 80% !important;
}

@media screen and (max-width: 767px) {
  .flex {
    display: inherit;
  }

  .requested-characters {
    display: block;
    margin-bottom: 0.5em;
  }
}
</style>